import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import {
  FileText,
  List,
  Minus,
  MessageSquare,
  Table2,
  BarChart3,
  TableProperties,
  GripVertical,
  Target,
  Users,
  DollarSign,
  Building,
  TrendingUp,
  FileSpreadsheet,
  Calculator,
  LayoutDashboard,
  Zap,
  Activity,
  TrendingDown,
  Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

// Block type definitions - organized by purpose
const BLOCK_CATEGORIES = [
  {
    name: 'Document Structure',
    blocks: [
      { type: 'cover_page', name: 'Cover Page', icon: FileText, description: 'Title page with branding' },
      { type: 'table_of_contents', name: 'Table of Contents', icon: List, description: 'Auto-generated TOC' },
      { type: 'page_break', name: 'Page Break', icon: Minus, description: 'Force new page' }
    ]
  },
  {
    name: 'Content Blocks',
    blocks: [
      { type: 'narrative', name: 'Narrative / AI Text', icon: MessageSquare, description: 'AI-generated or custom text' },
      { type: 'chart', name: 'Chart', icon: BarChart3, description: 'Visual data representation' },
      { type: 'custom_table', name: 'Custom Table', icon: TableProperties, description: 'Create your own table' }
    ]
  },
  {
    name: 'Business Plan Inputs',
    description: 'Include data from your business plan input sections',
    blocks: [
      { type: 'section_strategic_framework', name: 'Strategic Framework', icon: Target, description: 'Objectives, Initiatives, Products, Sales', sectionType: 'input' },
      { type: 'section_cost_centers', name: 'Cost Centers', icon: Users, description: 'Cost centers & allocations', sectionType: 'input' },
      { type: 'section_cost_management', name: 'Cost Management', icon: DollarSign, description: 'Core costs, Operations, Investments', sectionType: 'input' },
      { type: 'section_financial_management', name: 'Financial Management', icon: Building, description: 'Current Debt, Equity, Capital Structure', sectionType: 'input' },
      { type: 'section_financial_planning', name: 'Financial Planning', icon: TrendingUp, description: 'Planned Debt, Equity, Milestones', sectionType: 'input' }
    ]
  },
  {
    name: 'Financial Outputs',
    description: 'Include calculated financial statements and analytics',
    blocks: [
      { type: 'section_financial_statements', name: 'Financial Statements', icon: FileSpreadsheet, description: 'Income, Balance Sheet, Cash Flow', sectionType: 'output' },
      { type: 'section_financial_analytics', name: 'Financial Analytics', icon: Calculator, description: 'Break-Even, ROI, Cash Flow Analysis', sectionType: 'output' },
      { type: 'section_planning_dashboard', name: 'Planning Dashboard', icon: LayoutDashboard, description: 'KPIs, Charts, Summaries', sectionType: 'output' },
      { type: 'section_scenario_modeling', name: 'Scenario Modeling', icon: Zap, description: 'Scenarios & Comparisons', sectionType: 'output' }
    ]
  },
  {
    name: 'Performance Tracking',
    description: 'Include actual performance data and analysis',
    blocks: [
      { type: 'section_executive_dashboard', name: 'Executive Dashboard', icon: Activity, description: 'KPI overview & Performance summary', sectionType: 'performance' },
      { type: 'section_revenue_analysis', name: 'Revenue Analysis', icon: TrendingUp, description: 'Actual revenue & Comparisons', sectionType: 'performance' },
      { type: 'section_cost_analysis', name: 'Cost Analysis', icon: TrendingDown, description: 'Actual costs & Trends', sectionType: 'performance' },
      { type: 'section_profitability', name: 'Profitability Analysis', icon: DollarSign, description: 'Margins & P&L comparison', sectionType: 'performance' },
      { type: 'section_variance', name: 'Variance Analytics', icon: BarChart3, description: 'Revenue/Cost/Profit variances', sectionType: 'performance' },
      { type: 'section_trends', name: 'Trends & Forecasting', icon: TrendingUp, description: 'Historical trends & Projections', sectionType: 'performance' }
    ]
  },
  {
    name: 'Company Information',
    blocks: [
      { type: 'section_company_profile', name: 'Company Profile', icon: Settings, description: 'Company details & Team info', sectionType: 'company' }
    ]
  },
  {
    name: 'Legacy Data Blocks',
    description: 'Direct data source selection (advanced)',
    collapsed: true,
    blocks: [
      { type: 'data_section', name: 'Data Section', icon: Table2, description: 'Single data source selection' }
    ]
  }
];

// Draggable block item
const DraggableBlock = ({ block }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `palette-${block.type}`
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`
  } : undefined;

  const Icon = block.icon;
  
  // Color coding based on section type
  const getBgColor = () => {
    switch (block.sectionType) {
      case 'input': return 'bg-blue-50 border-blue-200';
      case 'output': return 'bg-green-50 border-green-200';
      case 'performance': return 'bg-purple-50 border-purple-200';
      case 'company': return 'bg-amber-50 border-amber-200';
      default: return 'bg-white';
    }
  };
  
  const getIconBgColor = () => {
    switch (block.sectionType) {
      case 'input': return 'bg-blue-100 text-blue-600';
      case 'output': return 'bg-green-100 text-green-600';
      case 'performance': return 'bg-purple-100 text-purple-600';
      case 'company': return 'bg-amber-100 text-amber-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`
        flex items-center gap-3 p-3 rounded-lg border cursor-grab
        hover:shadow-sm transition-all
        ${getBgColor()}
        ${isDragging ? 'opacity-50 shadow-lg border-blue-500' : ''}
      `}
    >
      <div className={`flex items-center justify-center w-8 h-8 rounded-md ${getIconBgColor()}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{block.name}</p>
        <p className="text-xs text-gray-500 truncate">{block.description}</p>
      </div>
      <GripVertical className="h-4 w-4 text-gray-300" />
    </div>
  );
};

const BlockPalette = ({ dataSources }) => {
  const [collapsedCategories, setCollapsedCategories] = React.useState(['Legacy Data Blocks']);
  
  const toggleCategory = (categoryName) => {
    setCollapsedCategories(prev => 
      prev.includes(categoryName) 
        ? prev.filter(c => c !== categoryName)
        : [...prev, categoryName]
    );
  };
  
  return (
    <Card className="sticky top-20 max-h-[calc(100vh-120px)] overflow-y-auto">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Report Blocks</CardTitle>
        <p className="text-xs text-gray-500">Drag blocks to build your report</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {BLOCK_CATEGORIES.map((category) => {
          const isCollapsed = collapsedCategories.includes(category.name);
          
          return (
            <div key={category.name}>
              <button 
                onClick={() => category.collapsed !== undefined && toggleCategory(category.name)}
                className="flex items-center justify-between w-full text-left mb-2"
              >
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {category.name}
                  </h4>
                  {category.description && (
                    <p className="text-xs text-gray-400 mt-0.5">{category.description}</p>
                  )}
                </div>
                {category.collapsed !== undefined && (
                  <span className="text-xs text-gray-400">
                    {isCollapsed ? '▸' : '▾'}
                  </span>
                )}
              </button>
              
              {!isCollapsed && (
                <div className="space-y-2">
                  {category.blocks.map((block) => (
                    <DraggableBlock key={block.type} block={block} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
        
        <div className="pt-4 border-t">
          <p className="text-xs text-gray-400 text-center">
            Drag blocks to the structure panel →
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default BlockPalette;
